import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Icon, toast } from '@livable/saas-react-components';
import { Button, Divider, ListSubheader, Menu, MenuItem } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useStore } from 'src/stores';
import { chargeExportPresetFor } from 'src/utils/company';
import { ChargeExportTemplatesModal } from './ChargeExportTemplatesModal';
export const ChargeExportButton = observer(_ref => {
  let propertyId = _ref.propertyId,
    year = _ref.year,
    month = _ref.month;
  const store = useStore();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    anchorEl = _useState2[0],
    setAnchorEl = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isManaging = _useState4[0],
    setIsManaging = _useState4[1];
  const inFlight = store.report.downloadChargeExportAsync.inProgress;

  // The preset the company's PMS switches on, if any. Resolved from the Company details value
  // rather than read off it: the label a PM picks there is not the key the export sends.
  const preset = chargeExportPresetFor(store.company.propertyManagementSoftware);
  const templates = store.chargeExportTemplates.templatesFor(propertyId);
  const defaultTemplate = store.chargeExportTemplates.effectiveDefaultFor(propertyId);
  useEffect(() => {
    // Loaded up front, not on menu open: the main button's label and what it exports both depend on
    // whether this property has a default template.
    store.chargeExportTemplates.getTemplatesAsync.run({
      propertyId
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);
  const handleDownload = async function () {
    var _resp$headers$unmappe, _resp$headers, _resp$headers$fileNa, _resp$headers2;
    let _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      format = _ref2.format,
      templateId = _ref2.templateId;
    setAnchorEl(null);
    // createAsync (auto: true) surfaces its own error toast and resolves to undefined on failure,
    // so guard the response instead of adding a second toast.
    const resp = await store.report.downloadChargeExportAsync.run({
      propertyId,
      year,
      month,
      format,
      templateId
    });
    if (!(resp !== null && resp !== void 0 && resp.data)) return;

    // An unmapped unit exports an empty Unit Id, which the PMS import rejects. Said here rather
    // than left for the PM to discover halfway through their import.
    const unmappedUnits = Number((_resp$headers$unmappe = (_resp$headers = resp.headers) === null || _resp$headers === void 0 ? void 0 : _resp$headers['unmapped-units']) !== null && _resp$headers$unmappe !== void 0 ? _resp$headers$unmappe : 0);
    if (unmappedUnits > 0) {
      toast('warning', {
        body: ["".concat(unmappedUnits === 1 ? '1 unit has' : "".concat(unmappedUnits, " units have"), " no PMS unit id. ") + 'Those rows export with an empty Unit Id — map them in the property’s unit settings.']
      });
    }
    const url = window.URL.createObjectURL(new Blob([resp.data], {
      type: 'application/octet-stream'
    }));
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', (_resp$headers$fileNa = (_resp$headers2 = resp.headers) === null || _resp$headers2 === void 0 ? void 0 : _resp$headers2['file-name']) !== null && _resp$headers$fileNa !== void 0 ? _resp$headers$fileNa : "charges_".concat(year, "-").concat(month, ".csv"));
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // What the main button does, in the order the PRD wants: a saved default beats the PMS preset,
  // which beats the Full report.
  const primaryAction = () => {
    var _preset$format;
    if (defaultTemplate) return handleDownload({
      templateId: defaultTemplate.id
    });
    return handleDownload({
      format: (_preset$format = preset === null || preset === void 0 ? void 0 : preset.format) !== null && _preset$format !== void 0 ? _preset$format : 'full'
    });
  };
  const primaryLabel = () => {
    if (defaultTemplate) return "Export (".concat(defaultTemplate.name, ")");
    if (preset) return "Export (".concat(preset.label, ")");
    return 'Export charges';
  };
  const templatesModal = /*#__PURE__*/React.createElement(ChargeExportTemplatesModal, {
    isOpen: isManaging,
    onClose: () => setIsManaging(false),
    propertyId: propertyId,
    onTemplatesChanged: () => store.chargeExportTemplates.getTemplatesAsync.run({
      propertyId
    })
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    variant: "actionButton",
    disabled: inFlight,
    onClick: primaryAction
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "file-text"
  }), /*#__PURE__*/React.createElement("span", null, primaryLabel())), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButton",
    disabled: inFlight,
    "aria-label": "export format options",
    onClick: e => setAnchorEl(e.currentTarget)
    // The theme puts 10px between two adjacent actionButtons. These two are one split control —
    // an action and its chooser — so they sit flush instead.
    ,
    sx: {
      marginLeft: '2px !important',
      minWidth: '32px',
      paddingLeft: '6px',
      paddingRight: '6px'
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "triangle-down"
  })), /*#__PURE__*/React.createElement(Menu, {
    anchorEl: anchorEl,
    open: Boolean(anchorEl),
    onClose: () => setAnchorEl(null)
  }, templates.length > 0 ? /*#__PURE__*/React.createElement(ListSubheader, null, "Saved templates") : null, templates.map(template => /*#__PURE__*/React.createElement(MenuItem, {
    key: template.id,
    onClick: () => handleDownload({
      templateId: template.id
    })
  }, template.isEffectiveDefault ? "".concat(template.name, " (default)") : template.name)), templates.length > 0 ? /*#__PURE__*/React.createElement(Divider, null) : null, preset ? /*#__PURE__*/React.createElement(MenuItem, {
    onClick: () => handleDownload({
      format: preset.format
    })
  }, "".concat(preset.label, " format")) : null, /*#__PURE__*/React.createElement(MenuItem, {
    onClick: () => handleDownload({
      format: 'full'
    })
  }, "Full report"), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(MenuItem, {
    onClick: () => {
      setAnchorEl(null);
      setIsManaging(true);
    }
  }, "Manage templates\u2026")), templatesModal);
});